import * as Plot from '@observablehq/plot';
import type { CustomExperimentSpec, PanelRow } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createChartCard } from '../render/chart-card';
import { createPopulationGrid } from '../render/population-grid';
import type { PopulationGridHandle } from '../render/population-grid';
import {
  createDataTable,
  type DataTableColumn,
  type DataTableHandle,
  type DataTableStat,
} from '@learnforge/demo-kit';
import { mountActionBar } from '../action-bar';
import type { ActionBarHandle } from '../action-bar';
import { POPULATIONS } from '../data';
import { sample, mean, stdDev } from '../statistics';
import type { SamplingTechnique, SampleOptions } from '../statistics';
import { formatNumber } from '@learnforge/demo-math/numeric';
import type { StatsContext, DemoKey } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PopItem {
  value: number;
  stratum: string;
  idx: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POPULATION_KEYS = ['gaussian', 'right-skewed', 'bimodal', 'uniform'] as const;
type PopulationKey = (typeof POPULATION_KEYS)[number];

const POPULATION_LABEL_KEYS: Record<PopulationKey, DemoKey> = {
  gaussian: 'population.gaussian.label',
  'right-skewed': 'population.right-skewed.label',
  bimodal: 'population.bimodal.label',
  uniform: 'population.uniform.label',
};

const TECHNIQUE_KEYS: SamplingTechnique[] = [
  'simple-random',
  'stratified',
  'systematic',
  'biased-convenience',
];

const TECHNIQUE_LABEL_KEYS: Record<SamplingTechnique, DemoKey> = {
  'simple-random': 'technique.simple-random.label',
  stratified: 'technique.stratified.label',
  systematic: 'technique.systematic.label',
  'biased-convenience': 'technique.biased-convenience.label',
};

const SAMPLE_SIZES = [5, 10, 20, 50, 100, 200, 500, 1000] as const;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface State {
  populationKey: PopulationKey;
  technique: SamplingTechnique;
  sampleSize: number;
  biasParam: number;
  sampleMeans: number[];
}

// ---------------------------------------------------------------------------
// Spec factory
// ---------------------------------------------------------------------------

function buildSpec(): CustomExperimentSpec<State, DemoKey, StatsContext> {
  return {
    labelKey: 'exp.sampling-lab.title',
    demoId: 'statistics',
    advancedKey: 'sampling-lab',

    initialState(): State {
      return {
        populationKey: 'gaussian',
        technique: 'simple-random',
        sampleSize: 50,
        biasParam: 0.5,
        sampleMeans: [],
      };
    },

    panel: (state, ctx): PanelRow<DemoKey, State>[] => {
      const rows: PanelRow<DemoKey, State>[] = [
        {
          kind: 'dropdown',
          id: 'sl-population',
          labelKey: 'param.population.label',
          options: POPULATION_KEYS.map((k) => ({ value: k, labelKey: POPULATION_LABEL_KEYS[k] })),
          value: state.populationKey,
          onChange: (v) => (prev) => ({
            ...prev,
            populationKey: v as PopulationKey,
            sampleMeans: [],
          }),
        },
        {
          kind: 'dropdown',
          id: 'sl-technique',
          labelKey: 'param.technique.label',
          options: TECHNIQUE_KEYS.map((k) => ({ value: k, labelKey: TECHNIQUE_LABEL_KEYS[k] })),
          value: state.technique,
          onChange: (v) => (prev) => ({
            ...prev,
            technique: v as SamplingTechnique,
            sampleMeans: [],
          }),
        },
        {
          kind: 'slider',
          id: 'sl-size',
          labelKey: 'param.sample-size.label',
          min: 0,
          max: SAMPLE_SIZES.length - 1,
          step: 1,
          value: SAMPLE_SIZES.indexOf(state.sampleSize as (typeof SAMPLE_SIZES)[number]),
          format: (i) => String(SAMPLE_SIZES[i]),
          // Updater form: 'biased-convenience' shows a second slider (sl-bias)
          // alongside this one, and slider input skips the panel rebuild — so
          // this closure can go stale if the bias slider moves first.
          onChange: (i) => (prev) => ({ ...prev, sampleSize: SAMPLE_SIZES[i]!, sampleMeans: [] }),
        },
      ];

      if (state.technique === 'stratified') {
        rows.push({
          kind: 'custom',
          render: () => renderStratumRow(state, ctx.i18n),
        });
      }

      if (state.technique === 'biased-convenience') {
        rows.push({
          kind: 'slider',
          id: 'sl-bias',
          labelKey: 'param.bias.label',
          min: 0,
          max: 1,
          step: 0.05,
          value: state.biasParam,
          format: (v) => v.toFixed(2),
          onChange: (v) => (prev) => ({ ...prev, biasParam: v, sampleMeans: [] }),
        });
      }

      return rows;
    },

    createRender(ctx, host, controls) {
      // ── Transient closure vars (NOT in State) ──────────────────────────────
      let intervalHandle: ReturnType<typeof setInterval> | null = null;
      let lastPopulationKey: PopulationKey = 'gaussian';

      // `computeStats()`/`getRows()` below read these on every table refresh;
      // `update()` keeps them in sync with the live state before calling
      // `dataTable.update()` (mirrors the currentXs/currentColumnKey guard
      // used by two-variable-lab/distribution-sculptor).
      let currentSampleMeans: number[] = [];
      let currentPopulationKey: PopulationKey = 'gaussian';

      // ── Root grid ──────────────────────────────────────────────────────────
      const grid = document.createElement('div');
      grid.className = 'stats-grid';
      host.appendChild(grid);

      // ── Data table (sample means, draw order) ───────────────────────────────
      function computeStats(): DataTableStat[] {
        const currentPop = POPULATIONS[currentPopulationKey]!;
        const trueMean = mean(currentPop.values);
        const latestMean =
          currentSampleMeans.length > 0 ? currentSampleMeans[currentSampleMeans.length - 1]! : NaN;
        const sdMeans = stdDev(currentSampleMeans);
        const count = currentSampleMeans.length;

        return [
          {
            label: ctx.i18n.t('stat.population-mean'),
            value: formatNumber(trueMean, { decimals: 3, trimIntegers: true }),
          },
          {
            label: ctx.i18n.t('stat.latest-sample-mean'),
            value: formatNumber(latestMean, { decimals: 3, trimIntegers: true }),
          },
          {
            label: ctx.i18n.t('stat.sample-mean-spread'),
            value: formatNumber(sdMeans, { decimals: 3, trimIntegers: true }),
          },
          { label: ctx.i18n.t('stat.sample-count'), value: String(count) },
        ];
      }

      const tableColumns: DataTableColumn<DemoKey>[] = [
        { id: 'sample', labelKey: 'table.sample' },
        {
          id: 'mean',
          labelKey: 'stat.mean',
          format: (v) => formatNumber(v as number, { decimals: 3, trimIntegers: true }),
        },
      ];
      const dataTable: DataTableHandle = createDataTable<DemoKey>({
        i18n: ctx.i18n,
        columns: tableColumns,
        getRows: () => currentSampleMeans.map((m, i) => ({ sample: i + 1, mean: m })),
        getStats: computeStats,
        rowNumbers: false,
      });

      // ── Chart cards — DOM order: data table, population, distribution ──────
      const tableCard = createChartCard<DemoKey>({
        titleKey: 'table.data',
        i18n: ctx.i18n,
        headerActions: [dataTable.controls],
        collapsible: { storageKey: 'learnforge:collapsible:statistics:sampling-lab:table' },
      });
      const popCard = createChartCard<DemoKey>({
        titleKey: 'card.population',
        i18n: ctx.i18n,
        className: 'pop-card',
        collapsible: { storageKey: 'learnforge:collapsible:statistics:sampling-lab:population' },
      });
      const distCard = createChartCard<DemoKey>({
        titleKey: 'card.sampling-distribution',
        i18n: ctx.i18n,
        className: 'dist-card',
        collapsible: {
          storageKey: 'learnforge:collapsible:statistics:sampling-lab:distribution',
        },
      });

      grid.append(tableCard.element, popCard.element, distCard.element);
      tableCard.setBody(dataTable.element);

      // ── Population grid ────────────────────────────────────────────────────
      const initialPop = POPULATIONS['gaussian']!;
      let gridHandle: PopulationGridHandle = createPopulationGrid({
        container: popCard.body,
        values: initialPop.values,
      });

      // ── Action bar (bespoke — not part of the declarative panel migration) ──
      const actionBar: ActionBarHandle = mountActionBar(ctx.actionBar, ctx.i18n);
      actionBar.setButtons([
        {
          id: 'resample',
          labelKey: 'btn.resample',
          onClick: () => {
            controls.setState((s) => {
              const indices = drawOneSampleFromState(s);
              gridHandle.highlight(indices.indices);
              return { ...s, sampleMeans: indices.updatedMeans };
            });
          },
        },
        {
          id: 'run-100',
          labelKey: 'btn.run-100',
          visible: () => intervalHandle === null,
          onClick: () => {
            startRun100();
          },
        },
        {
          id: 'stop',
          labelKey: 'btn.stop',
          visible: () => intervalHandle !== null,
          onClick: () => {
            stopRun100();
          },
        },
        {
          id: 'clear-history',
          labelKey: 'btn.clear-history',
          onClick: () => {
            gridHandle.highlight([]);
            controls.setState((s) => ({ ...s, sampleMeans: [] }));
          },
        },
      ]);

      // ── Helpers ────────────────────────────────────────────────────────────

      /**
       * Draw one sample from the given state, returning the highlighted indices
       * and the updated sampleMeans array (immutable). Uses Math.random — this
       * is pre-existing behaviour; no seeded prng is introduced here (out of
       * scope for this harness-only migration).
       */
      function drawOneSampleFromState(s: State): {
        indices: number[];
        updatedMeans: number[];
      } {
        const currentPop = POPULATIONS[s.populationKey]!;
        const values = currentPop.values;
        const strata = currentPop.strata;

        const items: PopItem[] = values.map((v, i) => ({
          value: v,
          stratum: strata?.[i] ?? '',
          idx: i,
        }));

        const samplingOpts: SampleOptions<PopItem> = {};
        if (s.technique === 'stratified') {
          samplingOpts.strataKey = (item) => item.stratum;
        } else if (s.technique === 'biased-convenience') {
          samplingOpts.biasKey = (item) => {
            if (s.biasParam === 0) return 1;
            return Math.pow(Math.max(item.value, 0), s.biasParam);
          };
        }
        const sampled = sample(items, s.sampleSize, s.technique, samplingOpts);

        const indices = sampled.map((sm) => sm.idx);
        const sampleMean = mean(sampled.map((sm) => sm.value));
        const updatedMeans = !Number.isNaN(sampleMean)
          ? [...s.sampleMeans, sampleMean]
          : s.sampleMeans;

        return { indices, updatedMeans };
      }

      function startRun100(): void {
        let count = 0;
        intervalHandle = setInterval(() => {
          controls.setState((s) => {
            const { indices, updatedMeans } = drawOneSampleFromState(s);
            gridHandle.highlight(indices);
            return { ...s, sampleMeans: updatedMeans };
          });
          count++;
          if (count >= 100) {
            stopRun100();
          }
        }, 20);
        // Refresh button visibility immediately
        actionBar.refresh();
      }

      function stopRun100(): void {
        if (intervalHandle !== null) {
          clearInterval(intervalHandle);
          intervalHandle = null;
        }
        actionBar.refresh();
      }

      // Register interval teardown with the harness
      controls.registerCleanup(() => {
        if (intervalHandle !== null) {
          clearInterval(intervalHandle);
          intervalHandle = null;
        }
      });

      // ── RenderHandle ───────────────────────────────────────────────────────

      function update(state: State): void {
        // Rebuild population grid only when populationKey changes
        if (state.populationKey !== lastPopulationKey) {
          lastPopulationKey = state.populationKey;
          gridHandle.destroy();
          const nextPop = POPULATIONS[state.populationKey]!;
          gridHandle = createPopulationGrid({
            container: popCard.body,
            values: nextPop.values,
          });
        }

        // Any panel change resets the run (sampleMeans back to []) — clear the
        // grid's stale highlight from a previous run. A rebuilt grid (above)
        // already starts unhighlighted, so this is a no-op in that case.
        if (state.sampleMeans.length === 0) {
          gridHandle.highlight([]);
        }

        currentSampleMeans = state.sampleMeans;
        currentPopulationKey = state.populationKey;
        dataTable.update();

        renderDistCard(state);
      }

      function renderDistCard(state: State): void {
        if (state.sampleMeans.length === 0) {
          const msg = document.createElement('div');
          msg.className = 'empty-card-explainer';
          msg.textContent = ctx.i18n.t('message.no-data');
          distCard.setBody(msg);
          return;
        }

        const plotEl = Plot.plot({
          marks: [
            Plot.frame({ anchor: 'left' }),
            Plot.rectY(state.sampleMeans, Plot.binX({ y: 'count' }, { x: (d: number) => d })),
            Plot.ruleY([0]),
          ],
          height: 200,
        }) as HTMLElement | SVGElement;

        distCard.setBody(plotEl as unknown as Node);
      }

      return {
        update,
        dispose() {
          stopRun100();
          gridHandle.destroy();
          dataTable.destroy();
          tableCard.destroy();
          popCard.destroy();
          distCard.destroy();
          actionBar.destroy();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

// ---------------------------------------------------------------------------
// Stratum row (custom panel row — options are population data values, not
// translatable i18n keys, so this isn't a declarative 'dropdown' row).
// ---------------------------------------------------------------------------

function renderStratumRow(state: State, i18n: StatsContext['i18n']): HTMLElement {
  const group = document.createElement('div');
  group.className = 'sx-field-group';

  const label = document.createElement('label');
  label.textContent = i18n.t('param.stratum.label');
  group.appendChild(label);

  const pop = POPULATIONS[state.populationKey];
  const uniqueStrata = pop?.strata ? Array.from(new Set(pop.strata)) : [];

  if (uniqueStrata.length === 0) {
    const note = document.createElement('span');
    note.className = 'sl-stratum-note';
    note.textContent = i18n.t('message.no-strata');
    group.appendChild(note);
  } else {
    group.classList.add('sx-dropdown-row');
    const select = document.createElement('select');
    select.className = 'fig-select';
    for (const s of uniqueStrata) {
      const opt = document.createElement('option');
      opt.value = s;
      opt.textContent = s;
      select.appendChild(opt);
    }
    group.appendChild(select);
  }

  return group;
}

// ---------------------------------------------------------------------------
// Public experiment + backwards-compat alias
// ---------------------------------------------------------------------------

export const experiment = defineExperiment(buildSpec());
export const mountSamplingLab = experiment.mount.bind(experiment);
